import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from "../../../config";
import {
  getTransformedDataForPagination,
  LocalStorageKeys,
  objectToQueryParams,
} from "../../../utils";

export interface Notification {
  created_time: string; // ISO date-time format
  created_user: string; // Name of the user who created the object
  created_user_id: string; // Unique ID of the user who created the object
  modified_time: string; // ISO date-time format
  modified_user: string; // Name of the user who modified the object
  modified_user_id: string; // Unique ID of the user who modified the object
  _id: string; // 24-character hexadecimal unique string
  delivery?: NotificationDelivery;
  event: NotificationEvent;
  message: string; // Notification message
  receiver: UserInfo;
  severity: NotificationSeverity;
}

interface NotificationDelivery {
  email?: {
    email_id?: string | null; // Nullable Email ID
  };
  in_app?: {
    bookmarked: boolean; // Indicates if the notification is bookmarked
    read_time?: string | null; // Nullable read timestamp
    message: string;
  };
}

interface NotificationEvent extends Omit<Event, "type"> {
  details: Record<string, any>; // Details of the event (Generic Object)
  type: NotificationEventType; // Type of event
}

export type NotificationEventType =
  | "CREATED"
  | "UPDATED"
  | "VERSION_UPDATED"
  | "CLONED"
  | "DELETED"
  | "PUBLISHED"
  | "ATTACHMENT_ADDED"
  | "ATTACHMENT_REMOVED"
  | "ACCESS_GRANTED"
  | "ACCESS_REVOKED"
  | "USER_ASSIGNED"
  | "USER_UNASSIGNED";

interface UserInfo {
  user_email: string; // Email ID of the user
  user_id: string; // User ID
  user_name: string; // Name of the user
}

type NotificationSeverity = 0 | 1 | 2;

type NotificationStatus = "read" | "unread";

interface NotificationQueryParams {
  status?: NotificationStatus; // Status filter (optional)
  object_type?: string; // Type of the associated object (optional)
  object_id?: string; // Unique identifier of the associated object (optional)
  page_no?: number; // Page number (optional)
  page_size?: number; // Number of documents per page (optional)
  before_time?: string; // Filter notifications before this timestamp (ISO date-time, optional)
  after_time?: string; // Filter notifications after this timestamp (optional)
}

interface SuccessResponse {
  sequence_id?: string; // ReadOnly
  version?: number; // ReadOnly
  _id: string; // Required, Must follow pattern: ^[a-f\d]{24}$
  message: string;
  url?: string; // Optional URL for document upload/download
}

export const notificationsAPI = createApi({
    reducerPath: "notificationsAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "notifications",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),
    tagTypes: ['Notifications'],
    endpoints: (builder) => ({
        // Get Tasks API
        getNotifications: builder.query<{ data: Notification[], totalCount: number }, NotificationQueryParams>({
            query: (payload) => ({ url: objectToQueryParams(payload), method: "GET" }),
            transformResponse: (data: Notification[], meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),

        // Get Notification by ID API
        getNotificationById: builder.query<Notification, string>({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
        }),

        // Update Notification API
        updateNotification: builder.mutation<SuccessResponse, { id: string, status: NotificationStatus }>({
            query: (payload) => ({ url: `/${payload.id}`, method: "PUT", body: { status: payload.status } }),
        }),

        // Delete Notification API
        deleteNotification: builder.mutation<string, string>({
            query: (id) => ({ url: `/${id}`, method: "DELETE" }),
        }),

        // Delete All Notification API
        deleteAllNotification: builder.mutation<string, { timePeriod?: { before_time: string, after_time: string } }>({
            query: (payload) => ({ method: "DELETE", url: objectToQueryParams(payload.timePeriod) }),
        }),
    })
})

// Export hooks for usage in functional components
export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useLazyGetNotificationByIdQuery,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationMutation
} = notificationsAPI
